/**
 * `/currency/rates` — each currency's newest rate against the base, whether
 * imports skip it, and the two ways a rate gets in: an import from a source,
 * or one typed by hand. Behind `currency.admin`.
 *
 * The import's result stays on screen until the next one, so "skipped,
 * locked" is read rather than missed.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CURRENCY_BASE, type CurrencyRateSummaryDto, type ImportRatesResponse } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { listRateSummaries, listSources, setLocked } from './api';
import { currencyName, sourceLabel } from './format';
import { ImportDialog } from './ImportDialog';
import { RateDialog } from './RateDialog';

export default function RatesPage() {
  const { t } = useTranslation('currency');
  const rates = useAsync(listRateSummaries, []);
  const sources = useAsync(listSources, []);

  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<ImportRatesResponse | null>(null);
  /** Undefined: closed. Null: open, pick a currency. */
  const [editing, setEditing] = useState<string | null | undefined>(undefined);

  const lock = useSubmit(async (currency: string, locked: boolean) => {
    const result = await setLocked(currency, locked);
    rates.reload();
    return result;
  });

  const today = sources.data?.defaultRange.to ?? '';

  return (
    <>
      <PageHeader
        title={t('rates.title')}
        description={t('rates.description', { base: CURRENCY_BASE })}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<EditOutlinedIcon />} disabled={!sources.data} onClick={() => setEditing(null)}>
              {t('rates.setRate')}
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudDownloadOutlinedIcon />}
              disabled={!sources.data}
              onClick={() => setImporting(true)}
            >
              {t('import.open')}
            </Button>
          </Stack>
        }
      />

      {imported && (
        <Alert severity={imported.skippedLocked.length > 0 ? 'info' : 'success'} onClose={() => setImported(null)} sx={{ mb: 2 }}>
          {t('import.result', {
            count: imported.written,
            days: imported.days,
            source: sourceLabel(imported.source, sources.data?.sources ?? [], t('source.manual')),
            from: imported.from,
            to: imported.to,
          })}
          {imported.skippedLocked.length > 0 && (
            <Typography variant="body2">{t('import.skippedLocked', { currencies: imported.skippedLocked.join(', ') })}</Typography>
          )}
          {imported.skippedUnknown.length > 0 && (
            <Typography variant="body2">{t('import.skippedUnknown', { currencies: imported.skippedUnknown.join(', ') })}</Typography>
          )}
        </Alert>
      )}
      {sources.error && <ErrorState error={sources.error} />}
      {lock.error && <ErrorState error={lock.error} />}

      <AsyncContent state={rates}>
        {({ currencies }) => (
          <EntityTable<CurrencyRateSummaryDto>
            rows={currencies}
            rowKey={(row) => row.currency}
            empty={{
              icon: <CurrencyExchangeIcon fontSize="inherit" />,
              title: t('rates.emptyTitle'),
              description: t('rates.emptyDescription'),
            }}
            columns={[
              {
                field: 'currency',
                label: t('field.currency'),
                render: (row) => (
                  <Stack>
                    <Link component={RouterLink} to={`/currency/rates/${row.currency}`} underline="hover" sx={{ fontWeight: 500 }}>
                      {row.currency}
                    </Link>
                    <Typography variant="caption" color="text.secondary">
                      {currencyName(row.currency)}
                    </Typography>
                  </Stack>
                ),
              },
              {
                field: 'rate',
                label: t('field.rate'),
                align: 'right',
                render: (row) =>
                  row.latest ? t('rates.perUnit', { currency: row.currency, rate: row.latest.rate, base: CURRENCY_BASE }) : '',
              },
              { field: 'date', label: t('field.date'), width: 130, render: (row) => row.latest?.date ?? '' },
              {
                field: 'source',
                label: t('field.source'),
                width: 200,
                render: (row) => row.latest && sourceLabel(row.latest.source, sources.data?.sources ?? [], t('source.manual')),
              },
              {
                field: 'locked',
                label: t('field.locked'),
                width: 130,
                render: (row) =>
                  row.locked && <Chip size="small" icon={<LockOutlinedIcon />} label={t('lock.locked')} variant="outlined" />,
              },
            ]}
            actions={(row) => [
              {
                key: 'set',
                label: t('rates.setRateFor', { currency: row.currency }),
                icon: <EditOutlinedIcon fontSize="small" />,
                disabled: !sources.data,
                onClick: () => setEditing(row.currency),
              },
              row.locked
                ? {
                    key: 'unlock',
                    label: t('lock.unlock'),
                    icon: <LockOpenOutlinedIcon fontSize="small" />,
                    disabled: lock.pending,
                    onClick: () => void lock.submit(row.currency, false),
                  }
                : {
                    key: 'lock',
                    label: t('lock.lock'),
                    icon: <LockOutlinedIcon fontSize="small" />,
                    disabled: lock.pending,
                    onClick: () => void lock.submit(row.currency, true),
                  },
            ]}
            actionsWidth={110}
          />
        )}
      </AsyncContent>

      {sources.data && (
        <ImportDialog
          open={importing}
          sources={sources.data.sources}
          defaultRange={sources.data.defaultRange}
          onClose={() => setImporting(false)}
          onImported={(result) => {
            setImported(result);
            rates.reload();
            sources.reload();
          }}
        />
      )}
      <RateDialog
        open={editing !== undefined}
        currency={editing ?? null}
        initialDate={today}
        onClose={() => setEditing(undefined)}
        onSaved={() => rates.reload()}
      />
    </>
  );
}
